import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import useSeo from '../hooks/useSeo';
import { usePageContent } from './home/pageContent';
import { BoxIcon, PageHero, PageShell, SectionIntro } from './home/blocks';
import { Kicker } from './home/ui';

const FIELD =
  'w-full rounded-[10px] border border-[#DBDBDB] bg-white px-[clamp(14px,1.1111vw,20px)] text-[clamp(16px,1vw,18px)] text-[#273845] outline-none transition-colors placeholder:text-brand-muted focus:border-brand focus:ring-4 focus:ring-brand/15';
const LABEL = 'mb-2 block text-[clamp(15px,1vw,18px)] font-semibold text-[#273845]';

/* Built from the Figma "Functionaliteiten" blocks. Content: Admin → Landingspagina → Contact. */
const Contact = () => {
  const { t } = useTranslation();
  const { lang, cms, content } = usePageContent('contact');
  const { info, form } = content;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ name: '', email: '', subject: '', message: '', priority: 'medium' });

  useSeo({
    path: '/contact',
    lang,
    title: cms?.seo?.title || t('contact_page.seo_title'),
    description: cms?.seo?.description || t('contact_page.seo_description'),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact WowProfit.io',
      mainEntity: { '@type': 'Organization', name: 'WowProfit.io', email: info.email },
    },
  });

  const change = (e) => setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/support/contact', data);
      toast.success(form.success);
      setData({ name: '', email: '', subject: '', message: '', priority: 'medium' });
    } catch (error) {
      toast.error(error.response?.data?.detail || form.error);
    } finally {
      setLoading(false);
    }
  };

  const rows = [
    { icon: 'mail', label: info.email_label, value: <a href={`mailto:${info.email}`} className="hover:text-brand">{info.email}</a> },
    { icon: 'clock', label: info.response_label, value: info.response_value },
    { icon: 'map', label: info.office_label, value: info.office_value },
  ];

  return (
    <PageShell testId="contact-page">
      <PageHero hero={content.hero} />
      <SectionIntro />

      <section className="hp-container mb-[clamp(56px,6.6667vw,120px)] mt-[clamp(28px,4vw,72px)] grid grid-cols-1 gap-[clamp(24px,2.7778vw,50px)] lg:grid-cols-[5fr_7fr]">
        <div className="hp-card hp-card-navy bg-brand-mint px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(36px,3.5556vw,64px)] lg:py-[clamp(48px,4.4444vw,80px)]" data-testid="contact-info">
          <ul className="space-y-[clamp(24px,2.2222vw,40px)]">
            {rows.map((r) => (
              <li key={r.icon} className="flex items-start gap-[clamp(14px,1.3333vw,24px)]">
                <span className="rounded-full bg-white">
                  <BoxIcon icon={r.icon} size="md" />
                </span>
                <div className="pt-1">
                  <Kicker className="text-brand-ink">{r.label}</Kicker>
                  <p className="mt-3 text-[clamp(17px,1.2222vw,22px)] font-semibold leading-snug text-[#273845]">{r.value}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={submit}
          className="hp-card hp-card-navy bg-white px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(36px,3.5556vw,64px)] lg:py-[clamp(48px,4.4444vw,80px)]"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-[clamp(16px,1.3333vw,24px)] sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className={LABEL}>{form.name}</label>
              <input id="contact-name" name="name" required value={data.name} onChange={change} className={`${FIELD} h-[clamp(50px,3.3333vw,60px)]`} data-testid="contact-name-input" />
            </div>
            <div>
              <label htmlFor="contact-email" className={LABEL}>{form.email}</label>
              <input id="contact-email" name="email" type="email" required value={data.email} onChange={change} className={`${FIELD} h-[clamp(50px,3.3333vw,60px)]`} data-testid="contact-email-input" />
            </div>
          </div>
          <div className="mt-[clamp(16px,1.3333vw,24px)]">
            <label htmlFor="contact-subject" className={LABEL}>{form.subject}</label>
            <input id="contact-subject" name="subject" required value={data.subject} onChange={change} className={`${FIELD} h-[clamp(50px,3.3333vw,60px)]`} data-testid="contact-subject-input" />
          </div>
          <div className="mt-[clamp(16px,1.3333vw,24px)]">
            <label htmlFor="contact-message" className={LABEL}>{form.message}</label>
            <textarea id="contact-message" name="message" required rows={6} value={data.message} onChange={change} className={`${FIELD} py-4 leading-relaxed`} data-testid="contact-message-input" />
          </div>
          <button type="submit" disabled={loading} className="hp-btn hp-btn-solid mt-[clamp(24px,2.2222vw,40px)] w-full disabled:opacity-60 sm:w-auto" data-testid="contact-submit-button">
            {loading ? form.sending : form.submit}
            <span aria-hidden className="hp-arrow" />
          </button>
        </form>
      </section>
    </PageShell>
  );
};

export default Contact;
